import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { logger } from '@/config/logger';
import { secretDetail, tOTPDetail } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { useReviewPrizepoolStore } from '@/modules/review-prizepool';

import { Fields } from '../../../../constants';
import type { Secret } from '../../../../types';

const QUALIFYING_COPY_FIELDS: Fields[] = [Fields.PASSWORD, Fields.TOTP];

export function useCopySecretValue(id: Secret['id'], property: Fields) {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();

    const [inProgress, setInProgress] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleCopy = async () => {
        setInProgress(true);

        try {
            const clipboardItem = new ClipboardItem({
                'text/plain': fetchSecretValue(id, property).then(text => new Blob([text!], { type: 'text/plain' })),
            });

            await navigator.clipboard.write([clipboardItem]);

            dispatch(
                messagesActions.displaySuccessMessage({
                    message: formatMessage({ id: 'copied' }),
                    options: { duration: 1 },
                }),
            );

            setSuccess(true);

            if (QUALIFYING_COPY_FIELDS.includes(property)) {
                useReviewPrizepoolStore.getState().markQualifyingAction();
            }
        } catch (error) {
            dispatch(
                messagesActions.displayErrorMessage({
                    message: { id: 'error.secrets.copy' },
                }),
            );

            setSuccess(false);

            logger.error(error);
        }

        setInProgress(false);
    };

    return {
        inProgress,
        success,
        handleCopy,
    };
}

async function fetchSecretValue(id: Secret['id'], property: Fields) {
    try {
        if (property === 'TOTP') {
            const { data: totpCodes } = await tOTPDetail({ id });

            return totpCodes[0].code;
        } else {
            const { data } = await secretDetail({ id });

            // @ts-expect-error
            const value = data[property];
            return value;
        }
    } catch (error) {
        logger.error(error);
        throw error;
    }
}
